import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useInView } from 'react-intersection-observer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ThemeToggle from '../components/ThemeToggle';
import { toast } from 'sonner';
import { Lock, ExternalLink, LogOut, Crown, LayoutDashboard, Share2, Search, X, User as UserIcon, ArrowLeft, Home } from 'lucide-react';
import EnhancedPostCard from '../components/EnhancedPostCard';
import SkeletonPostCard from '../components/SkeletonPostCard';
import { WhatsappShareButton, TelegramShareButton, TwitterShareButton } from 'react-share';
import { API_URL } from '../config';

const API = API_URL;

const FeedPage = ({ user, onLogout, onUpdateUser }) => {
  const { feedSlug } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showKeyOptionsModal, setShowKeyOptionsModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [feed, setFeed] = useState(null);
  const { ref, inView } = useInView();

  const fetchPosts = useCallback(async (skip = 0, search = '') => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch feed info if not already loaded
      if (!feed && feedSlug) {
        const feedResponse = await axios.get(`${API}/feeds/${feedSlug}`, {
          headers
        });
        setFeed(feedResponse.data);
        
        // Set page title based on feed
        const feedName = feedResponse.data.name || feedSlug;
        document.title = `EbonyThroatQueens | ${feedName}`;
      } else if (!feedSlug) {
        document.title = 'EbonyThroatQueens | Home';
      }
      
      const response = await axios.get(`${API}/posts`, {
        params: { limit: 20, skip, search, feed_id: feedSlug || 'home' },
        headers
      });
      
      if (skip === 0) {
        setPosts(response.data);
      } else {
        setPosts(prev => [...prev, ...response.data]);
      }
      
      setHasMore(response.data.length === 20);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [feedSlug, feed]);

  useEffect(() => {
    fetchPosts();
    checkSubscriptionStatus();
  }, [fetchPosts]);

  const checkSubscriptionStatus = async () => {
    if (user?.is_premium) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionInfo(response.data);
        
        // Show reminder toast if needed
        if (response.data.needs_reminder && response.data.reminder_message) {
          toast.warning(response.data.reminder_message, {
            duration: 10000,
            important: true
          });
        }
      } catch (error) {
        console.error('Failed to check subscription status');
      }
    }
  };

  useEffect(() => {
    if (inView && hasMore && !loading && !isSearching && !searchQuery) {
      fetchPosts(posts.length, searchQuery);
    }
  }, [inView, hasMore, loading, posts.length, fetchPosts, isSearching, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setIsSearching(true);
    setHasMore(true);
    fetchPosts(0, searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setHasMore(true);
    fetchPosts(0, '');
  };

  const handleUnlock = async (post) => {
    // If user not logged in, redirect to login
    if (!user) {
      toast.error('Please login to access keys');
      window.location.href = '/';
      return;
    }
    
    // Premium posts require premium subscription
    if (post.post_type === 'premium' && !user.is_premium) {
      setShowPremiumModal(true);
      return;
    }
    
    // Free users get options: Linkvertise or Premium upgrade
    setSelectedPost(post);
    setShowKeyOptionsModal(true);
  };

  const completeUnlock = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/posts/${selectedPost.post_id}/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Post unlocked! Enjoy the content.');
      setShowUnlockModal(false);
      setShowKeyOptionsModal(false);
      fetchPosts(0, searchQuery);
    } catch (error) {
      toast.error('Failed to unlock post');
    }
  };

  const handleContinueWithAds = () => {
    // Directly open Linkvertise URL
    if (selectedPost?.linkvertise_url) {
      window.open(selectedPost.linkvertise_url, '_blank');
      setShowKeyOptionsModal(false);
      toast.info('Complete the ad task to unlock the key');
    } else {
      toast.error('Linkvertise URL not configured for this post');
    }
  };

  const handleGetPremium = () => {
    // Close options modal and show payment modal
    setShowKeyOptionsModal(false);
    setShowPremiumModal(true);
  };

  const handleUpgradePremium = async () => {
    try {
      const res = await axios.post(`${API}/subscription/create`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (!res.data?.approval_url) {
        console.error('PayPal response:', res.data);
        throw new Error(res.data?.detail || 'Subscription creation failed');
      }
      
      window.location.href = res.data.approval_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to create subscription');
    }
  };

  const handleReaction = async (postId, emoji) => {
    // Deprecated - keeping for backward compatibility
    toast.info('Use upvote/downvote buttons');
  };

  const handleVote = async (postId, voteType) => {
    try {
      const token = localStorage.getItem('token');
      
      if (voteType === null) {
        await axios.delete(`${API}/posts/${postId}/vote`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/posts/${postId}/vote?vote_type=${voteType}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Update local state
      setPosts(posts.map(p => {
        if (p.post_id === postId) {
          const oldVote = p.user_vote;
          let newUpvotes = p.upvotes || 0;
          let newDownvotes = p.downvotes || 0;

          if (voteType === null && oldVote) {
            if (oldVote === 'upvote') newUpvotes--;
            else newDownvotes--;
          } else if (voteType && oldVote !== voteType) {
            if (oldVote === 'upvote') newUpvotes--;
            else if (oldVote === 'downvote') newDownvotes--;
            
            if (voteType === 'upvote') newUpvotes++;
            else newDownvotes++;
          }

          return { ...p, user_vote: voteType, upvotes: newUpvotes, downvotes: newDownvotes };
        }
        return p;
      }));
    } catch (error) {
      toast.error('Failed to vote');
      throw error;
    }
  };

  const handleSave = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/posts/${postId}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPosts(posts.map(p => p.post_id === postId ? { ...p, is_saved: true } : p));
    } catch (error) {
      toast.error('Failed to save post');
      throw error;
    }
  };

  const handleUnsave = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/posts/${postId}/save`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPosts(posts.map(p => p.post_id === postId ? { ...p, is_saved: false } : p));
    } catch (error) {
      toast.error('Failed to unsave post');
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] dark:bg-[#050505] light:bg-[#F5F5F5]">
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 dark:bg-black/80 light:bg-white/90 border-b border-[#2A2A2A] dark:border-[#2A2A2A] light:border-gray-200"
        data-testid="feed-header"
      >
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => window.location.href = '/welcome'}
                variant="ghost"
                size="sm"
                className="text-[#EE1C4E] hover:text-white hover:bg-[#1A1A1A] p-2"
                data-testid="home-button"
                title="Go to Welcome Page"
              >
                <Home className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl font-black" style={{ color: '#EE1C4E', letterSpacing: '-0.01em' }}>
                {feedSlug === 'home' ? 'HOME' : feedSlug === 'eliteebony' ? 'PPV PACKS' : (feed?.name || 'FEED')}
              </h1>
            </div>
            
            <div className="flex items-center gap-2">
              {user?.is_premium && (
                <span
                  className="px-2 py-1 rounded-full text-lg flex items-center"
                  style={{ background: '#EE1C4E' }}
                  data-testid="user-vip-badge"
                  title="Premium Member"
                >
                  👑
                </span>
              )}
              
              <ThemeToggle />
              
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <Button
                  onClick={() => window.location.href = '/admin'}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                  data-testid="admin-dashboard-button"
                >
                  <LayoutDashboard className="w-4 h-4" />
                </Button>
              )}
              
              <Button
                onClick={() => window.location.href = '/profile'}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
                data-testid="profile-button"
              >
                <UserIcon className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={onLogout}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#EE1C4E]"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-10 bg-black/40 border-gray-700 text-white placeholder:text-gray-600 focus:border-[#EE1C4E] h-11"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                data-testid="clear-search-button"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </form>
          
          {/* Subscription Reminder Banner */}
          {subscriptionInfo?.needs_reminder && (
            <div 
              className="mt-3 p-3 rounded-lg flex items-center justify-between"
              style={{ background: '#FFD600', color: '#000' }}
              data-testid="subscription-reminder"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">⚠️</span>
                <div>
                  <p className="text-xs font-bold">Subscription Expiring Soon!</p>
                  <p className="text-xs">{subscriptionInfo.days_remaining} days remaining</p>
                </div>
              </div>
              <Button
                onClick={() => setShowPremiumModal(true)}
                className="h-8 px-4 text-xs font-bold rounded-full"
                style={{ background: '#000', color: '#FFD600' }}
              >
                Renew Now
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Feed */}
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="feed-container">
        {searchQuery && (
          <div className="text-sm text-gray-400 mb-2" data-testid="search-status">
            {isSearching ? 'Searching...' : `Search results for "${searchQuery}" (${posts.length} found)`}
          </div>
        )}
        
        {loading && posts.length === 0 ? (
          <>
            <SkeletonPostCard />
            <SkeletonPostCard />
            <SkeletonPostCard />
          </>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchQuery ? 'No posts found matching your search' : 'No posts yet'}
          </div>
        ) : (
          posts.map(post => (
            <EnhancedPostCard
              key={post.post_id}
              post={post}
              user={user}
              onUnlock={handleUnlock}
              onVote={handleVote}
              onSave={handleSave}
              onUnsave={handleUnsave}
            />
          ))
        )}
        
        {hasMore && !searchQuery && <div ref={ref} className="h-20" />}
        {!hasMore && posts.length > 0 && !searchQuery && (
          <div className="text-center py-8 text-gray-600 text-sm">No more posts</div>
        )}
      </main>

      {/* Key Options Modal (Continue with Ads or Get Premium) */}
      <Dialog open={showKeyOptionsModal} onOpenChange={setShowKeyOptionsModal}>
        <DialogContent
          className="max-w-md"
          style={{ background: '#121212', border: '1px solid #2A2A2A', color: 'white' }}
          data-testid="key-options-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: '#EE1C4E' }}>
              Access Key
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-400 text-sm text-center">
              Choose how you want to access the key for this post
            </p>
            
            <div className="space-y-3">
              {/* Continue with Ads Option */}
              <button
                onClick={handleContinueWithAds}
                className="w-full p-5 rounded-xl text-left hover:brightness-110 transition-all"
                style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}
                data-testid="continue-with-ads-button"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-bold text-lg">Continue with Ads</h3>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: '#2A2A2A', color: '#00C853' }}>
                    FREE
                  </span>
                </div>
                <p className="text-gray-500 text-sm mb-3">
                  Complete a quick ad task to unlock the key
                </p>
                <div className="text-xs text-gray-600">
                  • Takes ~30 seconds
                  <br />
                  • No payment required
                </div>
              </button>

              {/* Get Premium Option */}
              <button
                onClick={handleGetPremium}
                className="w-full p-5 rounded-xl text-left hover:brightness-110 transition-all relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #EE1C4E 0%, #C41139 100%)', border: 'none' }}
                data-testid="get-premium-button"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-bold text-lg flex items-center gap-2">
                    <Crown className="w-5 h-5" />
                    Get Premium
                  </h3>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>
                    AD-FREE
                  </span>
                </div>
                <p className="text-white/90 text-sm mb-3">
                  Instant access to all keys without ads
                </p>
                <div className="text-xs text-white/70">
                  • No ads ever
                  <br />
                  • Access all premium content
                  <br />
                  • Early access to new posts
                </div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unlock Modal (Mock Linkvertise) */}
      <Dialog open={showUnlockModal} onOpenChange={setShowUnlockModal}>
        <DialogContent
          className="max-w-md"
          style={{ background: '#121212', border: '1px solid #2A2A2A', color: 'white' }}
          data-testid="unlock-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: '#EE1C4E' }}>
              Unlock This Post
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">
              Complete this quick task to unlock the post for free!
            </p>
            
            <div
              className="p-6 rounded-xl text-center"
              style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}
            >
              <Lock className="w-12 h-12 mx-auto mb-3" style={{ color: '#EE1C4E' }} />
              <p className="text-sm text-gray-500 mb-4">
                Mock Linkvertise Task
                <br />
                (In production, user would complete ad task here)
              </p>
              
              <Button
                onClick={completeUnlock}
                className="w-full h-12 text-white font-bold rounded-full"
                style={{ background: '#EE1C4E' }}
                data-testid="complete-unlock-button"
              >
                Complete Task & Unlock
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Premium Upgrade Modal */}
      <Dialog open={showPremiumModal} onOpenChange={setShowPremiumModal}>
        <DialogContent
          className="max-w-md"
          style={{ background: '#121212', border: '1px solid #2A2A2A', color: 'white' }}
          data-testid="premium-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: '#EE1C4E' }}>
              Subscribe to Premium
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div
              className="p-6 rounded-xl"
              style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}
            >
              <Crown className="w-12 h-12 mx-auto mb-3" style={{ color: '#EE1C4E' }} />
              
              <div className="text-center mb-4">
                <h3 className="text-3xl font-black mb-1">$15.00/month</h3>
                <p className="text-xs text-gray-500">Cancel anytime • 30-day subscription</p>
              </div>
              
              <div className="mb-4 p-3 rounded-lg" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
                <p className="text-xs text-gray-500 mb-1">Send payment to:</p>
                <p className="text-white font-bold text-sm">@Flowmedia4u</p>
                <p className="text-xs text-gray-600 mt-1">PayPal Username</p>
              </div>
              
              <h4 className="text-sm font-bold mb-3 text-center text-gray-300">What&apos;s Included:</h4>
              
              <ul className="space-y-2 text-sm text-gray-400 mb-6">
                <li className="flex items-center gap-2">
                  <span style={{ color: '#EE1C4E' }}>✓</span> Direct key access (no ads)
                </li>
                <li className="flex items-center gap-2">
                  <span style={{ color: '#EE1C4E' }}>✓</span> Access all premium posts
                </li>
                <li className="flex items-center gap-2">
                  <span style={{ color: '#EE1C4E' }}>✓</span> Early access to new content
                </li>
                <li className="flex items-center gap-2">
                  <span style={{ color: '#EE1C4E' }}>✓</span> 30-day subscription
                </li>
              </ul>
              
              <Button
                onClick={handleUpgradePremium}
                className="w-full h-12 text-white font-bold rounded-full"
                style={{ background: '#EE1C4E' }}
                data-testid="upgrade-premium-button"
              >
                Pay $15 via PayPal
              </Button>
              
              <p className="text-xs text-gray-600 text-center mt-3">
                After payment, provide transaction details for verification
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedPage;